<template>
  <div>
    <page-header
      icon="mdi-history"
      :title="
        toLatin($store.state.user.user.name) +
        ' ' +
        toLatin($store.state.user.user.lastname)
      "
      :subtitle="$t('order_history')"
    />
    <div
      class="hero"
      :style="`background: url('https://app.restorani.bitlab.host/gallery/${restaurant.id}/medium/${restaurant.settings.header}'); background-size: cover; background-position: center;`"
    >
      <img
        class="hero-logo"
        :src="
          conf.apiUrlBase + '/gallery/' + restaurant.id + '/' + restaurant.settings.logo
        "
      />
    </div>

    <v-container style="max-width: 1170px">
      <v-data-table
        dense
        class="ordera-box-shadow striped-table"
        :headers="headers"
        :items="meals"
        :loading="isLoadingHistory"
        @click:row="openDialog"
      >
        <template
          v-for="header in headers"
          v-slot:[`header.${header.value}`]="{ header }"
        >
          {{ translate(header.text) }}
        </template>
        <template v-slot:item.meals="{ item }">
          <p class="naziv-obroka">{{ translate(item.meals.name) }}</p>
        </template>
        <template v-slot:item.num="{ item, index }">
          {{ index + 1 }}
        </template>
        <template v-slot:item.rating="{ item }">
          <v-rating
            v-if="item.rating"
            :value="item.rating.rating"
            readonly
            dense
            small
            color="amber"
            background-color="grey lighten-1"
          />
          <v-btn
            v-else-if="item.can_rate"
            x-small
            outlined
            color="primary"
            @click.stop="openRatingDialog(item)"
          >
            <v-icon left x-small>mdi-star-outline</v-icon>
            {{ $t("meal_rating.rate") }}
          </v-btn>
        </template>
      </v-data-table>

      <global-dialog-card :max-width="800" v-model="dialog" title="" hide-actions>
        <v-row>
          <v-col cols="12" sm="6">
            <v-img
              :src="selectedMealImage || './media/img/no-image.webp'"
            >
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular
                    indeterminate
                    color="grey lighten-5"
                  ></v-progress-circular>
                </v-row>
              </template>
            </v-img>
          </v-col>
          <v-col cols="12" sm="6" class="ml-sm-n16 my-sm-2 mt-sm-16">
            <v-row align="end" class="fill-height">
              <v-card elevation="0" class="pa-5">
                <v-card-title
                  ><h3>
                    {{ translate(selectedMeal.meals.name) }}
                  </h3>
                </v-card-title>

                <v-card-text class="mb-4">
                  <v-row no-gutters>
                    <template v-for="(eat, i) in selectedMeal.meals.foods">
                      <v-col cols="12" class="font-weight-bold pt-2">
                        <v-row no-gutters justify="space-between">
                          <span>
                            <h3>{{ translate(eat.name) }}</h3>
                          </span>
                        </v-row>
                      </v-col>
                      <v-col cols="12">{{ translate(eat.description) }}</v-col>
                    </template>
                  </v-row>
                </v-card-text>

                <v-card-text v-if="selectedMeal.picked_up" class="pt-0">
                  <v-divider class="mb-4" />
                  <template v-if="selectedMeal.rating">
                    <h4 class="mb-1">{{ $t("meal_rating.your_rating") }}</h4>
                    <div class="d-flex align-center">
                      <v-rating
                        :value="selectedMeal.rating.rating"
                        readonly
                        dense
                        color="amber"
                        background-color="grey lighten-1"
                      />
                      <span class="ml-2 body-2 grey--text text--darken-1">
                        {{ $t(`meal_rating.labels.${selectedMeal.rating.rating}`) }}
                      </span>
                    </div>
                    <div v-if="selectedMeal.rating.issues && selectedMeal.rating.issues.length" class="mt-2">
                      <v-chip
                        v-for="code in selectedMeal.rating.issues"
                        :key="code"
                        small
                        outlined
                        class="mr-1 mb-1"
                      >
                        {{ $t(`meal_rating.issues.${code}`) }}
                      </v-chip>
                    </div>
                    <p v-if="selectedMeal.rating.comment" class="mt-2 mb-1">
                      {{ selectedMeal.rating.comment }}
                    </p>
                  </template>
                  <div class="caption grey--text">
                    {{
                      selectedMeal.can_rate
                        ? $t("meal_rating.deadline_until", {
                            date: formatDeadline(selectedMeal.rating_deadline),
                          })
                        : $t("meal_rating.deadline_expired")
                    }}
                  </div>
                  <v-btn
                    v-if="selectedMeal.can_rate"
                    class="mt-3"
                    color="primary"
                    depressed
                    @click="openRatingDialog(selectedMeal)"
                  >
                    <v-icon left>mdi-star-outline</v-icon>
                    {{
                      selectedMeal.rating
                        ? $t("meal_rating.update")
                        : $t("meal_rating.rate_meal")
                    }}
                  </v-btn>
                </v-card-text>
                <v-card-text v-else class="pt-0 grey--text">
                  {{ $t("meal_rating.only_picked_up") }}
                </v-card-text>
              </v-card>
            </v-row>
          </v-col>
        </v-row>
      </global-dialog-card>

      <meal-rating-dialog
        v-model="ratingDialog"
        :order="ratingOrder"
        @saved="onRatingSaved"
      />
    </v-container>
  </div>
</template>

<script>
import conf from "@/config";
import hasSnackbar from "@/mixins/hasSnackbar";
import userService from "@/services/user";
import moment from "moment";
import MealRatingDialog from "@/components/companyMenu/MealRatingDialog.vue";
import { orderMealImageUrl } from "@/utils/mealImage";

export default {
  mixins: [hasSnackbar],
  components: { MealRatingDialog },
  data() {
    return {
      meals: [],
      dialog: false,
      conf: conf,
      selectedMeal: { meals: { name: null, foods: {} } },
      counter: 0,
      isLoadingHistory: false,
      ratingDialog: false,
      ratingOrder: null,
      headers: [
        {
          text: this.$t("serial_number"),
          value: "num",
        },
        {
          text: this.$t("date"),
          value: "date",
        },
        {
          text: this.$t("shift.shift"),
          value: "shift",
        },
        {
          text: this.$t("meal"),
          value: "meals",
        },
        {
          text: this.$t("meal_rating.rating"),
          value: "rating",
          sortable: false,
        },
      ],
    };
  },
  computed: {
    selectedMealImage() {
      return orderMealImageUrl(this.selectedMeal);
    },
    restaurant() {
      return this.$store.state.restaurant
        ? this.$store.state.restaurant
        : this.$store.state.company.restaurant;
    },
  },
  created() {
    this.isLoadingHistory = true;
    userService
      .getUserHistory()
      .then((res) => {
        let modified = [];
        res.data.forEach((element) => {
          element.shift = element.shift.replace("-smena", "");
          modified.push(element);
        });

        this.meals = modified;
      })
      .catch((error) => {
        this.showErrorSnackbar(error, this.$t("error_loading_history"));
      })
      .finally(() => {
        this.isLoadingHistory = false;
      });
  },
  methods: {
    openDialog(item) {
      this.dialog = true;
      this.selectedMeal = item;
    },
    openRatingDialog(item) {
      this.ratingOrder = item;
      this.ratingDialog = true;
    },
    onRatingSaved(rating) {
      this.$set(this.ratingOrder, "rating", rating);
    },
    formatDeadline(value) {
      return value ? moment(value).format("DD.MM.YYYY. HH:mm") : "";
    },
  },
};
</script>

<style scoped>
/* .row {
  width: 100%;
} */

.istorija-header {
  font-weight: bold;
  font-size: 19px;
}

.red-istorija {
  cursor: pointer;
}

.red-istorija p {
  margin: 0;
  padding: 0;
}

.red-istorija:nth-child(odd) {
  background: #f3f4f4;
}

.naziv-obroka {
  cursor: pointer;
  transition: all 0.2s;
  margin: 0;
  width: max-content;
  padding: 5px 15px;
}

.naziv-obroka:hover {
  font-weight: bold;
  background: #ededed;
  padding: 5px 14px;
  width: max-content;
}
</style>
